import { ArrowRight, Check } from "lucide-react";
import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import CTASection from "@/components/CTASection";
import { Reveal } from "@/components/Reveal";
import { SERVICES, IMG } from "@/data/site";

export default function ServicePage({ id }) {
  const s = SERVICES[id];
  if (!s) return null;

  return (
    <>
      <PageHero eyebrow={s.eyebrow} title={s.title} lead={s.intro} />

      <section className="py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16">
          <div>
            {s.features.map((f, i) => (
              <Reveal key={f.title} delay={i * 0.06}>
                <div className="border-l-2 border-line hover:border-gold pl-6 py-6 group">
                  <h2 className="font-display text-xl sm:text-2xl font-bold uppercase text-slate-100 flex items-center gap-3">
                    <span className="font-mono text-xs text-gold tracking-[0.25em]">
                      0{i + 1}
                    </span>
                    {f.title}
                  </h2>
                  <p className="text-slate-400 mt-3 leading-relaxed">{f.text}</p>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.2}>
              <Link
                to="/contact"
                data-testid={`service-${id}-cta-button`}
                className="mt-6 inline-flex items-center gap-2 bg-gold px-6 py-3.5 text-sm font-bold uppercase tracking-wider text-ink hover:bg-gold-light transition-colors"
              >
                Request a Free Estimate
                <ArrowRight size={15} aria-hidden="true" />
              </Link>
            </Reveal>
          </div>
          <div className="space-y-6">
            {s.beforeAfter && (
              <Reveal>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    ["Before", s.beforeAfter.before],
                    ["After", s.beforeAfter.after],
                  ].map(([label, img]) => (
                    <div key={label} className="relative border border-line overflow-hidden">
                      <img
                        src={IMG(img.src)}
                        alt={img.alt}
                        loading="lazy"
                        className="w-full aspect-[3/4] object-cover"
                      />
                      <span className="absolute top-3 left-3 bg-ink/90 border border-gold/60 text-gold font-mono text-[10px] font-bold uppercase tracking-[0.2em] px-2.5 py-1">
                        {label}
                      </span>
                    </div>
                  ))}
                </div>
              </Reveal>
            )}
            {!s.beforeAfter &&
              s.images.map((img, i) => (
                <Reveal key={img.src} delay={i * 0.08}>
                  <div className="overflow-hidden border border-line">
                    <img
                      src={IMG(img.src)}
                      alt={img.alt}
                      loading="lazy"
                      className="w-full aspect-[4/3] object-cover hover:scale-[1.03] transition-transform duration-500"
                    />
                  </div>
                </Reveal>
              ))}
            <Reveal delay={0.15}>
              <div className="bg-coal border border-line p-6 flex items-start gap-4">
                <Check size={18} className="text-gold mt-1 shrink-0" aria-hidden="true" />
                <p className="text-sm text-slate-300 leading-relaxed">
                  Every job gets the same attention to detail — big or small, done right the first
                  time. Call us at{" "}
                  <a href="tel:+14062916639" data-testid={`service-${id}-call-link`} className="text-gold font-semibold">
                    (406) 291-6639
                  </a>{" "}
                  to discuss your project.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
